import React from "react";
import { Box, Button, Grid, Skeleton, Typography } from "@mui/material";
import {
  Add as AddIcon,
  CallSplitOutlined as SplitIcon,
  LocationOnOutlined as ZoneIcon,
  SortOutlined as FifoIcon,
  SwapVertOutlined as PriorityIcon,
  WarehouseOutlined as WarehouseIcon,
} from "@mui/icons-material";
import ActiveRuleCard from "./ActiveRuleCard";

export const INFO_ITEMS = [
  { icon: FifoIcon, label: "Lot Selection", desc: "Control which stock batches get picked first", color: "#667eea" },
  { icon: ZoneIcon, label: "Zone Routing", desc: "Match customer location to nearest warehouse", color: "#f6a623" },
  { icon: PriorityIcon, label: "Priority Order", desc: "Set a custom warehouse fulfillment sequence", color: "#a18cd1" },
  { icon: SplitIcon, label: "Split Orders", desc: "Allow partial fulfillment across multiple sites", color: "#30cfd0" },
];

function SkeletonCard() {
  return (
    <Box
      sx={{
        borderRadius: 3,
        border: "1.5px solid var(--border-color)",
        background: "var(--bg-paper)",
        p: 3,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton width="40%" height={24} />
          <Skeleton width="60%" height={16} sx={{ mt: 0.5 }} />
        </Box>
        <Skeleton variant="rounded" width={60} height={24} />
      </Box>
      <Grid container spacing={2}>
        {[...Array(4)].map((_, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Skeleton variant="rounded" height={64} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default function ClientView({ rules, loading, onEdit, onAdd }) {
  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        <SkeletonCard />
        <SkeletonCard />
      </Box>
    );
  }

  if (rules.length === 0) {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto", textAlign: "center", py: 8, px: 2 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: 4,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 3,
          }}
        >
          <WarehouseIcon sx={{ fontSize: 40, color: "#fff" }} />
        </Box>
        <Typography fontFamily="Poppins, sans-serif" fontSize={20} fontWeight={700} color="var(--text-primary)" mb={1}>
          No Fulfillment Rule Configured
        </Typography>
        <Typography fontFamily="Poppins, sans-serif" fontSize={13} color="var(--text-secondary)" mb={3}>
          Set up your fulfillment preferences to control how incoming orders are allocated to your
          warehouses — by lot age, location zone, or custom priority.
        </Typography>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{
            fontFamily: "Poppins, sans-serif",
            textTransform: "none",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            px: 4,
            py: 1.2,
            borderRadius: 2,
          }}
        >
          Configure Fulfillment Rule
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Box>
          <Typography fontFamily="Poppins, sans-serif" fontSize={15} fontWeight={700} color="var(--text-primary)">
            Your Fulfillment Rules
          </Typography>
          <Typography fontFamily="Poppins, sans-serif" fontSize={12} color="var(--text-secondary)">
            These rules control how orders are allocated during inventory planning
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{ fontFamily: "Poppins, sans-serif", textTransform: "none" }}
        >
          Add Rule
        </Button>
      </Box>
      {rules.map((rule) => (
        <ActiveRuleCard key={rule._id} rule={rule} onEdit={onEdit} />
      ))}
    </Box>
  );
}
